import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, Flex, Skeleton, Text } from 'vvs-uikit'
import { useWeb3React } from '@web3-react/core'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import sumBy from 'lodash/sumBy'
import { useAppSelector } from 'state'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useFarmTokensPricesUsdc } from 'state/farms/useFarmTokensPricesUsdc'
import { usePools } from 'state/pools/hooks'
import { fetchClpUserReward } from 'state/burn/queries/clpUserReward'
import { useTranslation } from 'contexts/Localization'
import { getBalanceNumber, getBalanceAmount } from 'utils/formatBalance'
import Tokens from 'config/constants/tokens'
import { getUsersHarvestedData, getUsersHarvestedV2Data } from 'hooks/useFarmGraph'
import { getUserSmartCraftData } from 'hooks/useSmartCraftGraph'
import LocalBalance from 'components/LocalBalance'
import useFarmsWithBalance from 'views/Home/hooks/useFarmsWithBalance'
import useFarmsV2WithBalance from 'views/Home/hooks/useFarmsV2WithBalance'
import useClpEarnings from 'views/Home/hooks/useClpEarnings'
import { fetchBoostReward } from 'state/xVvs/queries/boostReward'
import { UserHarvestedRewarderV2Record } from 'config/constants/types'

const StyledCard = styled(Card)`
  width: 100%;
  height: fit-content;
`

const PlatformYieldCard = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()

  const vvsPriceUsdc = usePriceVvsUsdc()
  const [harvestedSum, setHarvestedSum] = useState(0)
  const [clpUserReward, setClpUserReward] = useState(0)
  const [smartCraftData, setSmartCraftData] = useState([])
  const [rewarderData, setRewarderData] = useState<UserHarvestedRewarderV2Record[]>([])
  const [vvsBoostUserReward, setVvsBoostUserReward] = useState(0)
  const prices = useFarmTokensPricesUsdc((s) => s.value)
  const { earningsSum: farmEarningsSum } = useFarmsWithBalance()
  const { earningsUsdc: farmsV2EarningsUsdc } = useFarmsV2WithBalance()
  const pendingVVS = useClpEarnings()
  const boostPendingVVS = useAppSelector((s) => s.xVvs.boostData.pendingVvs)
  const { pools } = usePools()
  const [smartCraftReward, setSmartCraftReward] = useState(0)
  const [rewarderReward, setRewarderReward] = useState(0)

  const earningUsdcForMines = useMemo(
    () =>
      sumBy(
        pools
          .filter((pool) => pool.userData && new BigNumber(pool.userData.stakedBalance).isGreaterThan(0))
          .map(
            (pool) =>
              getBalanceNumber(pool.userData.pendingReward, pool.earningToken.decimals) * pool.earningTokenPrice,
          ),
      ),
    [pools],
  )

  useEffect(() => {
    const fetchHarvestedData = async () => {
      const [{ user: harvested }, { user: harvestedV2, userRewarders }] = await Promise.all([
        getUsersHarvestedData(account),
        getUsersHarvestedV2Data(account),
      ])
      if (
        harvested?.totalClaimAmount ||
        harvested?.totalVVSVaultProfit ||
        harvestedV2?.totalClaimAmount ||
        userRewarders
      ) {
        setRewarderData(userRewarders)
        setHarvestedSum(
          Number(harvested?.totalClaimAmount || 0) +
            Number(harvested?.totalVVSVaultProfit || 0) +
            Number(harvestedV2?.totalClaimAmount || 0),
        )
      }
    }
    const fetchSmartCraftData = async () => {
      const { data } = await getUserSmartCraftData(account)
      setSmartCraftData(data)
    }
    const fetchClpUserRewardData = async () => {
      const { data } = await fetchClpUserReward(account)
      if (data?.userReward?.totalVVSClaimed) {
        const userReward = getBalanceNumber(new BigNumber(data.userReward.totalVVSClaimed), Tokens.vvs.decimals)
        setClpUserReward(userReward)
      }
    }
    const fetchVvsBoostUserRewardData = async () => {
      const { data } = await fetchBoostReward(account)
      if (data?.vvsclaims[0]?.amount) {
        const userReward = sumBy(data?.vvsclaims, (claim) =>
          getBalanceNumber(new BigNumber(claim.amount), Tokens.vvs.decimals),
        )
        setVvsBoostUserReward(userReward)
      }
    }
    if (account) {
      fetchHarvestedData()
      fetchSmartCraftData()
      fetchClpUserRewardData()
      fetchVvsBoostUserRewardData()
    }
  }, [account])

  useEffect(() => {
    if (account && smartCraftData?.length > 0) {
      const smartCraftRewards = sumBy(smartCraftData, (reward) =>
        getBalanceNumber(new BigNumber(reward.amount * prices[reward.token.id]), reward.token.decimals),
      )
      setSmartCraftReward(smartCraftRewards)
    }
  }, [account, smartCraftData, prices])

  useEffect(() => {
    if (account && rewarderData?.length > 0) {
      const rewarderRewards = sumBy(rewarderData, (reward) =>
        getBalanceNumber(
          new BigNumber(reward.rewardTokenClaimed * prices[reward.rewarder.rewardToken]),
          reward.rewarder.rewardTokenDecimal,
        ),
      )
      setRewarderReward(rewarderRewards)
    }
  }, [account, rewarderData, prices])

  const totalPlatformYield = vvsPriceUsdc
    .multipliedBy(getBalanceAmount(new BigNumber(harvestedSum), Tokens.vvs.decimals))
    .plus(vvsPriceUsdc.multipliedBy(farmEarningsSum))
    .plus(vvsPriceUsdc.multipliedBy(pendingVVS))
    .plus(vvsPriceUsdc.multipliedBy(clpUserReward))
    .plus(vvsPriceUsdc.multipliedBy(vvsBoostUserReward))
    .plus(vvsPriceUsdc.multipliedBy(getBalanceAmount(new BigNumber(boostPendingVVS), Tokens.vvs.decimals)))
    .plus(farmsV2EarningsUsdc)
    .plus(earningUsdcForMines)
    .plus(smartCraftReward)
    .plus(rewarderReward)

  return (
    <StyledCard>
      <CardBody>
        <Flex
          flexDirection={['column', null, null, 'row']}
          justifyContent="space-between"
          alignItems={['flex-start', null, null, 'center']}
        >
          <Flex flexDirection="column" alignItems={['flex-start', null, null, 'flex-start']}>
            <Text color="blue" style={{ lineHeight: 1 }} bold fontSize="24px">
              {!totalPlatformYield.isNaN() ? (
                <LocalBalance
                  decimals={totalPlatformYield.gt(0) ? 2 : 0}
                  fontSize="24px"
                  bold
                  prefix={totalPlatformYield.gt(0) ? '~' : ''}
                  lineHeight="1.1"
                  value={totalPlatformYield}
                />
              ) : (
                <Skeleton width={96} height={24} my="2px" />
              )}
            </Text>
            <Text color="textSubtle" fontSize="13px" style={{ whiteSpace: 'nowrap' }}>
              {t('Total Platform Yield')}
            </Text>
          </Flex>
        </Flex>
      </CardBody>
    </StyledCard>
  )
}

export default PlatformYieldCard
